if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;
      quantityXObserver = undefined;
      quantityXUpdateFrame = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
          this.syncDisplayedVariantPrices(this.currentVariant);
        }
        this.initQuantityXPriceObserver();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.quantityXObserver?.disconnect();
        if (this.quantityXUpdateFrame) cancelAnimationFrame(this.quantityXUpdateFrame);
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.disableButtons();
        const selectedVariant = this.getVariantFromCurrentSelection();
        if (selectedVariant) {
          this.currentVariant = selectedVariant;
          this.updateVariantInputs(selectedVariant.id);
          this.syncDisplayedVariantPrices(selectedVariant);
        }
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            if (!variant) {
              this.setUnavailable();
              return;
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.currentVariant = variant;
          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku');
          updateSourceFromDestination('Inventory');
          updateSourceFromDestination('Badges');
          updateSourceFromDestination('PricePerItem');
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          updateSourceFromDestination('QuantityRules');
          updateSourceFromDestination('VolumeNote');
          this.syncDisplayedVariantPrices(variant);

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);
          if (stickyAtcBar) {
            stickyAtcBar.classList.remove('hidden');
          }

          const newAddButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const isDisabled = !newAddButton || newAddButton.hasAttribute('disabled');
          this.updateButtonsState(isDisabled, {
            updateText: true,
            text: isDisabled ? FoxTheme.variantStrings.soldOut : null,
          });

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getProductVariants() {
        const variants = this.variantSelectors?.querySelector('[data-product-variants]')?.textContent;
        if (!variants) return [];

        try {
          return JSON.parse(variants);
        } catch (error) {
          console.error(error);
          return [];
        }
      }

      getSelectedOptionNames() {
        if (!this.variantSelectors) return [];

        return Array.from(this.variantSelectors.querySelectorAll('.product-form__input')).map((option) => {
          const select = option.querySelector('select');
          if (select) return select.value;

          return option.querySelector('input:checked')?.value;
        });
      }

      getVariantFromCurrentSelection() {
        const selectedOptions = this.getSelectedOptionNames();
        if (!selectedOptions.length || selectedOptions.some((option) => option == null)) return null;

        return this.getProductVariants().find((variant) => {
          return variant.options.every((option, index) => option === selectedOptions[index]);
        });
      }

      getDisplayPrice(cents, discountPercent = 0) {
        const displayPriceMultiplier = Number(FoxTheme.settings.displayPriceMultiplier || 100);
        const discountMultiplier = 100 - discountPercent;
        const price = Math.floor((cents * displayPriceMultiplier * discountMultiplier) / 10000);
        return FoxTheme.Currency.formatMoney(price, FoxTheme.settings.moneyFormat);
      }

      getQuantityDiscountPercent(quantity) {
        if (quantity >= 500) return 15;
        if (quantity >= 250) return 10;
        if (quantity >= 100) return 5;
        return 0;
      }

      replaceMoney(text, money) {
        const moneyPattern = /(?:€|\$)\s?\d+(?:[,.]\d{2})?/;
        return moneyPattern.test(text) ? text.replace(moneyPattern, money) : money;
      }

      initQuantityXPriceObserver() {
        if (this.quantityXObserver) return;

        this.quantityXObserver = new MutationObserver(() => {
          if (this.quantityXUpdateFrame) cancelAnimationFrame(this.quantityXUpdateFrame);

          this.quantityXUpdateFrame = requestAnimationFrame(() => {
            const variant = this.currentVariant || this.getVariantFromCurrentSelection();
            if (variant) this.syncQuantityXPrices(variant);
          });
        });

        this.quantityXObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }

      getQuantityFromText(text) {
        const quantityMatch = text.match(/(?:da|from)?\s*(\d+)\s*(?:pezzi|pcs|\+)/i);
        if (!quantityMatch) return null;

        const quantity = parseInt(quantityMatch[1], 10);
        return Number.isFinite(quantity) ? quantity : null;
      }

      setElementText(element, text) {
        if (element && element.textContent.trim() !== text) element.textContent = text;
      }

      syncQuantityXPrices(variant) {
        const app = document.getElementById('quantity-discount-app');
        if (!app || !variant?.price) return;

        const baseMoney = this.getDisplayPrice(variant.price);
        const rows = app.querySelectorAll('.spec_offer_item_wrapper, .wrapper_choose_list_item, tbody tr');

        rows.forEach((row) => {
          const quantity = this.getQuantityFromText(row.textContent || '');
          if (!quantity) return;

          const discountPercent = this.getQuantityDiscountPercent(quantity);
          if (!discountPercent) return;

          const discountedMoney = this.getDisplayPrice(variant.price, discountPercent);
          const priceArea = row.querySelector('.right_content, .choose_list_prices, td:last-child') || row;
          let oldPrice = priceArea.querySelector('.old_price, .choose_list_prices_old, s');
          let newPrice = priceArea.querySelector('.new_price, .choose_list_prices_new, strong');

          if (!oldPrice || !newPrice) {
            const moneyElements = Array.from(priceArea.querySelectorAll('span, strong, s')).filter((element) =>
              /(?:€|\$)\s?\d+(?:[,.]\d{2})?/.test(element.textContent)
            );

            oldPrice = oldPrice || moneyElements[0];
            newPrice = newPrice || moneyElements[1] || moneyElements[0];
          }

          this.setElementText(oldPrice, baseMoney);
          this.setElementText(newPrice, discountedMoney);
        });
      }

      syncDisplayedVariantPrices(variant) {
        if (!variant?.price) return;

        const baseMoney = this.getDisplayPrice(variant.price);
        const price = this.querySelector(`#price-${this.dataset.section}`);
        if (price) {
          const regularItems = price.querySelectorAll('.f-price__regular .f-price-item--regular');
          const regularPrice = regularItems[regularItems.length - 1];
          if (regularPrice) regularPrice.textContent = baseMoney;

          const salePrice = price.querySelector('.f-price__sale .f-price-item--sale');
          if (salePrice?.textContent.trim()) salePrice.textContent = baseMoney;
        }

        const volumePricing = this.querySelector(`#Volume-${this.dataset.section}`);
        this.syncQuantityXPrices(variant);
        if (!volumePricing) return;

        volumePricing.querySelectorAll('li[data-volume-min-qty]').forEach((row) => {
          const quantity = parseInt(row.dataset.volumeMinQty, 10);
          if (!Number.isFinite(quantity)) return;

          const discountPercent = this.getQuantityDiscountPercent(quantity);
          const rowMoney = this.getDisplayPrice(variant.price, discountPercent);
          const priceContainer = row.querySelector('[data-volume-price]');
          if (!priceContainer) return;

          priceContainer.dataset.text = this.replaceMoney(priceContainer.dataset.text || priceContainer.textContent, rowMoney);

          const regularPrice = priceContainer.querySelector('s');
          const salePrice = priceContainer.querySelector('strong');
          if (regularPrice && salePrice) {
            regularPrice.textContent = baseMoney;
            salePrice.textContent = rowMoney;
          } else {
            priceContainer.textContent = this.replaceMoney(priceContainer.textContent, rowMoney);
          }
        });
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      updateButtonsState(disabled, options = {}) {
        const { updateText = false, text = null, updateStyles = true } = options;

        // Handle main add button in product form
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (productForm) {
          const addButton = productForm.querySelector('[name="add"]');
          const addButtonText = productForm.querySelector('[name="add"] > span');

          if (addButton) {
            if (disabled) {
              addButton.setAttribute('disabled', 'disabled');
              if (updateText && text && addButtonText) {
                addButtonText.textContent = this.decoded(text);
              }
            } else {
              const variantId = productForm.querySelector('[name="id"]')?.value;
              if (variantId) {
                addButton.removeAttribute('disabled');
                if (updateText && addButtonText) {
                  addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
                }
              }
            }

            if (updateStyles) {
              addButton.style.pointerEvents = disabled ? 'none' : '';
              addButton.style.opacity = disabled ? '0.6' : '';
            }
          }
        }

        // Handle sticky ATC button
        const otherButtonSelectors = ['.sticky-atc-bar [name="add"]'];
        otherButtonSelectors.forEach((selector) => {
          const buttons = document.querySelectorAll(selector);
          buttons.forEach((button) => {
            if (disabled) {
              button.setAttribute('disabled', 'disabled');
              if (updateStyles) {
                button.style.pointerEvents = 'none';
                button.style.opacity = '0.6';
              }
            } else {
              button.removeAttribute('disabled');
              if (updateStyles) {
                button.style.pointerEvents = '';
                button.style.opacity = '';
              }
            }
          });
        });
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.updateButtonsState(true, {
          updateText: true,
          text: FoxTheme.variantStrings.unavailable,
        });
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);
        const volumePricing = document.getElementById(`Volume-${this.dataset.section}`);
        const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
        if (volumePricing) volumePricing.classList.add('hidden');
        if (stickyAtcBar) stickyAtcBar.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      disableButtons() {
        this.updateButtonsState(true);
      }

      enableButtons() {
        this.updateButtonsState(false);
      }
    }
  );
}
